/**
 * Decode a JWT token and return its payload object.
 * Does NOT verify the signature (verification happens server-side).
 */
export function parseJwt(token) {
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );
    return JSON.parse(jsonPayload);
  } catch {
    return {};
  }
}

/**
 * Check if a JWT has expired based on the `exp` claim.
 * Returns false when `exp` is missing.
 */
export function isJwtExpired(token) {
  const payload = parseJwt(token);
  const exp = Number(payload?.exp);
  if (!Number.isFinite(exp)) return false;
  return Date.now() >= exp * 1000;
}

/**
 * Validate a stored token for route protection.
 * A token is valid when it can be decoded and is not expired.
 */
export function isTokenUsable(token) {
  if (!token || typeof token !== 'string') return false;
  const payload = parseJwt(token);
  if (!payload || Object.keys(payload).length === 0) return false;
  return !isJwtExpired(token);
}

/**
 * Extract an array of permission strings from a JWT payload.
 * Supports:
 *   - payload.permissions  → string[]  e.g. ["draw:read", "users:write"]
 *   - payload.roles        → string[]  e.g. ["admin", "editor"]
 *   - payload.scope        → string    e.g. "draw:read users:write"
 */
export function extractPermissions(payload) {
  const perms = new Set();

  const addValues = (value) => {
    if (Array.isArray(value)) {
      value.filter(Boolean).forEach(v => perms.add(v));
      return;
    }
    if (typeof value === 'string' && value.trim()) {
      // Support both a single permission string and space-separated scopes.
      value.split(' ').filter(Boolean).forEach(v => perms.add(v));
    }
  };

  if (Array.isArray(payload.permissions)) {
    payload.permissions.forEach(p => perms.add(p));
  }
  addValues(payload.permission);
  if (Array.isArray(payload.roles)) {
    payload.roles.forEach(r => perms.add(r));
  }
  addValues(payload.role);
  addValues(payload['http://schemas.microsoft.com/ws/2008/06/identity/claims/role']);
  if (typeof payload.scope === 'string') {
    payload.scope.split(' ').filter(Boolean).forEach(s => perms.add(s));
  }
  addValues(payload.scp);

  return [...perms];
}

/**
 * Read the stored JWT, decode it, and return the permissions array.
 * Returns an empty array if no token is stored or the token is malformed.
 */
export function getPermissionsFromToken() {
  const token = localStorage.getItem('token');
  if (!token) return [];
  const payload = parseJwt(token);
  return extractPermissions(payload);
}

/**
 * Read the current username from the JWT payload when available.
 */
export function getUsernameFromToken() {
  const token = localStorage.getItem('token');
  if (!token) return '';

  const payload = parseJwt(token);

  return payload.name
    || payload.unique_name
    || payload['http://schemas.xmlsoap.org/ws/2005/05/identity/claims/name']
    || '';
}

/**
 * Resolve the current username using JWT claims first, then local storage fallback.
 */
export function getCurrentUsername() {
  return getUsernameFromToken() || localStorage.getItem('user') || '';
}

const PROFILE_STORAGE_KEY = 'userProfile';

function readStoredProfile() {
  try {
    const rawValue = localStorage.getItem(PROFILE_STORAGE_KEY);
    return rawValue ? JSON.parse(rawValue) : {};
  } catch {
    return {};
  }
}

export function getStoredUserProfile() {
  return readStoredProfile();
}

export function saveStoredUserProfile(profile) {
  const currentProfile = readStoredProfile();
  const nextProfile = {
    ...currentProfile,
    ...profile,
  };

  localStorage.setItem(PROFILE_STORAGE_KEY, JSON.stringify(nextProfile));
  return nextProfile;
}

export function clearStoredUserProfile() {
  localStorage.removeItem(PROFILE_STORAGE_KEY);
}

export function getStoredAvatarUrl() {
  return readStoredProfile().avatarUrl || '';
}

export function resolveAvatarUrl(avatarUrl, apiBaseUrl) {
  if (!avatarUrl) {
    return '';
  }

  if (/^https?:\/\//i.test(avatarUrl)) {
    return avatarUrl;
  }

  if (avatarUrl.startsWith('/')) {
    return `${apiBaseUrl}${avatarUrl}`;
  }

  return `${apiBaseUrl}/${avatarUrl}`;
}

export async function hydrateStoredUserProfile(apiBaseUrl) {
  const token = localStorage.getItem('token');
  if (!isTokenUsable(token)) {
    return readStoredProfile();
  }

  try {
    const response = await fetch(`${apiBaseUrl}/api/User/me/profile`, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      return readStoredProfile();
    }

    const profile = await response.json();
    return saveStoredUserProfile({
      username: profile.username || getCurrentUsername(),
      avatarUrl: profile.avatarUrl || '',
    });
  } catch {
    return readStoredProfile();
  }
}
